import { Badge, DateTimeFormat } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { DocumentDetailsCard } from '@features/invoices/elements/DocumentDetailsCard'
import { RecurringInvoiceStatus } from '@features/recurringInvoices/enums/recurringInvoiceStatus'
import { getRecurringInvoiceDeliveryLabel } from '@features/recurringInvoices/utils/getRecurringInvoiceDeliveryLabel'
import { getRecurringInvoiceFrequencyLabel } from '@features/recurringInvoices/utils/getRecurringInvoiceFrequencyLabel'

import { getCurrentLocale } from '../../../../../../../../utils'
import { recurringInvoiceStatusToBadgeColor } from '../../../../../../constants/recurringInvoiceStatusToBadgeColor'
import { getRecurringInvoiceStatusLabel } from '../../../../../../utils/getRecurringInvoiceStatusLabel'
import { useRecurringInvoice } from '../../../../contexts/recurringInvoiceContext'
import { DetailContent } from './elements/DetailContent'

export const RecurringInvoiceDetailsCard = (): ReactElement | null => {
  const { t } = useTranslation()
  const { recurringInvoice, isLoading } = useRecurringInvoice()

  const locale = getCurrentLocale()

  const { status: statusData, schedule, lastInvoiceGeneratedDate } = recurringInvoice || {}
  const status = statusData?.status
  const startDate = schedule?.startDate ? new Date(schedule?.startDate) : undefined
  const lastInvoiceDate = lastInvoiceGeneratedDate ? new Date(lastInvoiceGeneratedDate) : undefined
  const nextInvoiceDate = statusData?.nextScheduleDate ? new Date(statusData.nextScheduleDate) : undefined

  const displayedStatus = getRecurringInvoiceStatusLabel(status, t)
  const displayedStartDate = startDate ? DateTimeFormat.getFormattedDate(startDate, locale) : undefined
  const displayedLastInvoiceDate = lastInvoiceDate
    ? DateTimeFormat.getFormattedDate(lastInvoiceDate, locale)
    : undefined
  const displayedNextInvoiceDate = nextInvoiceDate
    ? DateTimeFormat.getFormattedDate(nextInvoiceDate, locale)
    : undefined
  const displayedFrequency = getRecurringInvoiceFrequencyLabel(schedule?.frequency, t)
  const displayedDelivery = getRecurringInvoiceDeliveryLabel(schedule?.action, t)

  return (
    <DocumentDetailsCard isLoading={isLoading}>
      <DocumentDetailsCard.Entry title={t('recurring_invoices.page.details_card.status.header')}>
        <DetailContent value={displayedStatus}>
          <Badge color={recurringInvoiceStatusToBadgeColor[status || RecurringInvoiceStatus.All]}>
            {displayedStatus}
          </Badge>
        </DetailContent>
      </DocumentDetailsCard.Entry>

      <DocumentDetailsCard.Entry title={t('recurring_invoices.page.details_card.first_invoice.header')}>
        <DetailContent value={displayedStartDate} />
      </DocumentDetailsCard.Entry>

      {displayedNextInvoiceDate && (
        <DocumentDetailsCard.Entry title={t('recurring_invoices.page.details_card.next_invoice.header')}>
          <DetailContent value={displayedNextInvoiceDate} />
        </DocumentDetailsCard.Entry>
      )}

      <DocumentDetailsCard.Entry title={t('recurring_invoices.page.details_card.last_invoice.header')}>
        <DetailContent value={displayedLastInvoiceDate} />
      </DocumentDetailsCard.Entry>

      <DocumentDetailsCard.Entry title={t('recurring_invoices.page.details_card.frequency.header')}>
        <DetailContent value={displayedFrequency} />
      </DocumentDetailsCard.Entry>

      <DocumentDetailsCard.Entry title={t('recurring_invoices.page.details_card.delivery.header')}>
        <DetailContent value={displayedDelivery} />
      </DocumentDetailsCard.Entry>
    </DocumentDetailsCard>
  )
}
